import { useCallback, useState } from 'react';
import { DEFAULT_CARD_BACK } from './CardUtils';

interface CardBackProps {
  backImageUrl?: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}

export default function CardBack({
  backImageUrl,
  className = "",
  onClick
}: CardBackProps) {
  const actualBackImageUrl = backImageUrl || DEFAULT_CARD_BACK;
  const [isBackLoading, setIsBackLoading] = useState(true);
  const [backError, setBackError] = useState(false);

  // Handle back image loading
  const handleBackImageLoad = useCallback(() => {
    setIsBackLoading(false);
    setBackError(false);
  }, []);

  // Handle back image error
  const handleBackImageError = useCallback(() => {
    setIsBackLoading(false);
    setBackError(true);
  }, []);

  return (
    <div 
      className={`w-full h-full relative overflow-hidden ${className}`}
      onClick={onClick}
    >
      {/* Back image loading state */}
      {isBackLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-400">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      
      {/* Back image error state */}
      {backError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-600 text-white">
          <span className="text-xs">Card</span>
          <span className="text-xs mt-1">Back</span>
        </div>
      )}
      
      {/* Back image */}
      <img
        src={actualBackImageUrl}
        alt="Card Back"
        className="w-full h-full object-cover bg-slate-200"
        draggable={false}
        onLoad={handleBackImageLoad}
        onError={handleBackImageError}
        style={{ opacity: isBackLoading || backError ? 0 : 1 }}
      />
      
      {/* Opaque placeholder for when back image is not loaded or has error */}
      {(isBackLoading || backError) && (
        <div className="absolute inset-0 bg-slate-800 flex flex-col items-center justify-center">
          {/* Card back design with Dexsy branding */}
          <div className="w-full h-full relative overflow-hidden">
            {/* Border pattern */}
            <div className="absolute inset-0 border-4 border-amber-400/60"></div>
            <div className="absolute inset-2 border-2 border-slate-600"></div>

            {/* Center emblem */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="rounded-full w-12 h-12 bg-slate-500 flex items-center justify-center shadow-lg transform rotate-45">
                <div className="rounded-full w-10 h-10 bg-slate-700 flex items-center justify-center transform -rotate-45">
                  <span className="text-slate-500 font-bold text-md">D</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
